import { supabase } from '../utils/supabase';

/**
 * Fetch a player's ELO history based on filters
 * @param {string} playerId - Player ID
 * @param {string} clubId - Club ID
 * @param {string|null} seasonId - Season ID (optional)
 * @param {string} pairType - Pair type filter: 'all', 'mixed', or 'levels'
 * @returns {Promise<Array>} - Sorted array of ELO history records
 */
export const fetchPlayerEloHistory = async (playerId, clubId, seasonId = null, pairType = 'all') => {
  try {
    let query = supabase
      .from('elo_history')
      .select(`
        id,
        player_id,
        club_id,
        clubs:club_id(id, name),
        session_id,
        sessions:session_id(id, name, created_at),
        season_id,
        seasons:season_id(id, name, start_date, end_date),
        pair_type,
        game_type_id,
        old_value,
        new_value,
        elo_change,
        created_at,
        session_game_id,
        session_games:session_game_id(
          created_at,
          team_1_score,
          team_2_score,
          players_session_games (
            team_number,
            players (
              first_name,
              last_name
            )
          )
        )
      `)
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .order('created_at', { ascending: true });

    // Apply season filter if provided
    if (seasonId) {
      query = query.eq('season_id', seasonId);
    }

    // Apply pair type filter if not 'all'
    if (pairType !== 'all') {
      query = query.eq('pair_type', pairType);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Sort by game date (using session_games.created_at if available)
    return data.sort((a, b) => {
      const dateA = a.session_games?.created_at || a.created_at;
      const dateB = b.session_games?.created_at || b.created_at;
      return new Date(dateA) - new Date(dateB);
    });
  } catch (error) {
    console.error('Error fetching player ELO history:', error);
    throw error;
  }
};

/**
 * Get player's summary statistics
 * @param {string} playerId - Player ID
 * @param {string} clubId - Club ID (optional)
 * @param {string} seasonId - Season ID (optional)
 * @returns {Promise<Object>} - Player statistics
 */
export const fetchPlayerStats = async (playerId, clubId = null, seasonId = null) => {
  try {
    // Base query to get player data
    let query = supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        gender,
        player_club_elo_ratings!inner(
          mixed_elo_rating,
          levels_elo_rating,
          singles_elo_rating,
          club_id,
          season_id
        )
      `)
      .eq('id', playerId);

    // Apply club filter if provided
    if (clubId) {
      query = query.eq('player_club_elo_ratings.club_id', clubId);
    }

    // Apply season filter if provided
    if (seasonId) {
      query = query.eq('player_club_elo_ratings.season_id', seasonId);
    }

    const { data, error } = await query.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching player stats:', error);
    throw error;
  }
};

/**
 * Fetch player's recent games
 * @param {string} playerId - Player ID
 * @param {string} clubId - Club ID (optional)
 * @param {string} seasonId - Season ID (optional)
 * @param {number} limit - Maximum number of games to return
 * @returns {Promise<Array>} - Recent games
 */
/**
 * Fetch a player's unified game history (competitive + social games)
 * @param {string} playerId - Player ID
 * @param {string} clubId - Club ID (optional)
 * @param {string} seasonId - Season ID (optional)
 * @param {number} limit - Maximum number of games to return
 * @returns {Promise<Array>} - Unified game history
 */
export const fetchPlayerUnifiedGameHistory = async (playerId, clubId = null, seasonId = null, limit = 10, offset = 0) => {
  try {
    let query = supabase
      .from('player_game_history')
      .select('*')
      .eq('player_id', playerId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply club filter if provided
    if (clubId) {
      query = query.eq('club_id', clubId);
    }

    // Apply season filter if provided
    if (seasonId) {
      query = query.eq('season_id', seasonId);
    }

    const { data: games, error } = await query;

    if (error) throw error;

    if (!games || games.length === 0) {
      return [];
    }

    // Transform the data to match the expected format
    const transformedGames = games.map(game => ({
      id: game.game_id,
      created_at: game.created_at,
      session_game_id: game.game_id,
      player_id: game.player_id,
      club_id: game.club_id,
      team_number: game.team_number,
      result: game.result,
      pair_type: game.pair_type || 'levels',
      season_id: game.season_id,
      session_id: game.session_id,
      session_games: {
        id: game.game_id,
        team_1_score: game.team_1_score,
        team_2_score: game.team_2_score,
        created_at: game.created_at,
        club_id: game.club_id,
        session_id: game.session_id
      },
      game_type: game.game_type,
      session_type_name: game.session_type_name,
      game_type_name: game.game_type_name,
      club_name: game.club_name,
      clubName: game.club_name,
      sessionName: game.session_name,
      teammates: [], // Will be populated below
      opponents: [], // Will be populated below
      elo_change: null, // Will be fetched separately
      playerElo: null,
      eloChange: null,
      gameCreateMethod: null
    }));

    // Get all unique game IDs
    const gameIds = transformedGames
      .map(g => g.session_game_id)
      .filter(id => id !== null && id !== undefined);

    if (gameIds.length === 0) {
      return transformedGames;
        }
        
    // BATCH QUERY 1: Fetch ALL players for ALL games in one query
    const { data: allGamePlayers, error: playersError } = await supabase
          .from('players_session_games')
          .select(`
            id,
        session_game_id,
            player_id,
            team_number,
            players:player_id(
              id, 
              first_name, 
              last_name,
              gender,
              is_guest,
              is_active,
              merged_at,
              guest_merge_declined_at
            )
          `)
      .in('session_game_id', gameIds);
          
        if (playersError) {
          console.error('Error fetching game players:', playersError);
        }
        
    // BATCH QUERY 2: Fetch ALL ELO data for ALL games in one query
    const { data: allEloData, error: eloError } = await supabase
          .from('elo_history')
      .select('session_game_id, player_id, pair_type, old_value, new_value, elo_change')
      .in('session_game_id', gameIds)
      .eq('player_id', playerId);
          
        if (eloError) {
      console.error('Error fetching ELO data:', eloError);
        }
        
    // BATCH QUERY 3: Fetch ALL game_create_method for ALL games in one query
    const { data: allSessionGames, error: sessionGamesError } = await supabase
      .from('session_games')
      .select('id, game_create_method')
      .in('id', gameIds);

    if (sessionGamesError) {
      console.error('Error fetching session games:', sessionGamesError);
    }

    // Group data by game_id for fast lookup
    const playersByGame = {};
    if (allGamePlayers) {
      allGamePlayers.forEach(playerGame => {
        const gameId = playerGame.session_game_id;
        if (!playersByGame[gameId]) {
          playersByGame[gameId] = [];
        }
        playersByGame[gameId].push(playerGame);
      });
    }

    const eloByGame = {};
    if (allEloData) {
      allEloData.forEach(elo => {
        const gameId = elo.session_game_id;
        const key = `${gameId}_${elo.pair_type}`;
        if (!eloByGame[key]) {
          eloByGame[key] = elo;
        }
      });
    }

    const gameCreateMethodByGame = {};
    if (allSessionGames) {
      allSessionGames.forEach(sg => {
        gameCreateMethodByGame[sg.id] = sg.game_create_method;
      });
    }

    // Now populate each game with the batched data
    const gamesWithPlayers = transformedGames.map(game => {
      if (!game.session_game_id) {
        return { ...game, teammates: [], opponents: [] };
      }

      const gamePlayers = playersByGame[game.session_game_id] || [];
      const gameEloKey = `${game.session_game_id}_${game.pair_type}`;
      const eloRecord = eloByGame[gameEloKey] || null;
      const gameCreateMethod = gameCreateMethodByGame[game.session_game_id] || null;
        
        // Separate teammates and opponents
        const teammatePlayers = gamePlayers
          .filter(p => p.team_number === game.team_number && p.player_id !== game.player_id);
      const teammates = teammatePlayers.map(p => p.players).filter(Boolean);
        
        const opponents = gamePlayers
          .filter(p => p.team_number !== game.team_number)
        .map(p => p.players)
        .filter(Boolean);
        
        return { 
          ...game, 
          teammates, 
          opponents,
          playerElo: eloRecord?.old_value || null,
          eloChange: eloRecord?.elo_change || null,
          gameCreateMethod: gameCreateMethod
        };
    });

    return gamesWithPlayers;
  } catch (error) {
    console.error('Error fetching unified game history:', error);
    throw error;
  }
};

/**
 * Fetch player's club-specific statistics for a season
 * @param {string} playerId - Player ID
 * @param {string} clubId - Club ID
 * @param {string} seasonId - Season ID
 * @returns {Promise<Object>} - Player club statistics
 */
export const fetchPlayerClubStats = async (playerId, clubId, seasonId) => {
  try {
    // Fetch ELO ratings and game counts
    const { data: eloData, error: eloError } = await supabase
      .from('player_club_elo_ratings')
      .select(
        'mixed_elo_rating, levels_elo_rating, singles_elo_rating, mixed_games_played, levels_games_played, singles_games_played, unbalanced_games_played'
      )
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .eq('season_id', seasonId)
      .maybeSingle();

    if (eloError && eloError.code !== 'PGRST116') { // PGRST116 is "not found" which is okay
      throw eloError;
    }

    // Fetch last session date and sessions played based on games history (not signups)
    const { data: historyRows, error: historyError } = await supabase
      .from('player_game_history')
      .select('session_id, club_id, season_id, created_at')
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .eq('season_id', seasonId);

    let lastSessionDate = null;
    let sessionsCount = 0;

    if (!historyError && historyRows && historyRows.length > 0) {
      // Determine last session date from latest game
      const latest = historyRows.reduce((latestRow, row) => {
        if (!row.created_at) return latestRow;
        if (!latestRow) return row;
        return new Date(row.created_at) > new Date(latestRow.created_at)
          ? row
          : latestRow;
      }, null);

      if (latest && latest.created_at) {
        lastSessionDate = latest.created_at;
      }

      // Count distinct sessions in this season/club
      const sessionIds = new Set(
        historyRows
          .map((row) => row.session_id)
          .filter((id) => !!id)
      );
      sessionsCount = sessionIds.size;
    } else if (historyError) {
      console.error('Error fetching player game history for stats:', historyError);
    }

    // Get player info for gender and guest status
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .select('gender, is_guest')
      .eq('id', playerId)
      .single();

    if (playerError) {
      console.error('Error fetching player data:', playerError);
    }

    const totalGamesPlayed =
      (eloData?.mixed_games_played || 0) +
      (eloData?.levels_games_played || 0) +
      (eloData?.singles_games_played || 0) +
      (eloData?.unbalanced_games_played || 0);

    return {
      mixedElo: eloData?.mixed_elo_rating ?? 1200,
      levelsElo: eloData?.levels_elo_rating ?? 1200,
      singlesElo: eloData?.singles_elo_rating ?? 1000,
      lastSessionDate: lastSessionDate,
      sessionsPlayed: sessionsCount || 0,
      totalGamesPlayed,
      gender: playerData?.gender || null,
      isGuest: playerData?.is_guest || false
    };
  } catch (error) {
    console.error('Error fetching player club stats:', error);
    throw error;
  }
};

/**
 * Batch session summary for club player pickers (e.g. add-existing modal).
 * @returns {Promise<Record<string, { sessionsPlayed: number, lastSessionDate: string|null }>>}
 */
export async function fetchClubPlayersSessionSummary(playerIds, clubId, seasonId) {
  const ids = [...new Set((playerIds || []).filter(Boolean))];
  if (ids.length === 0 || !clubId || !seasonId) {
    return {};
  }

  const { data, error } = await supabase
    .from('player_game_history')
    .select('player_id, session_id, created_at')
    .eq('club_id', clubId)
    .eq('season_id', seasonId)
    .in('player_id', ids);

  if (error) {
    console.error('[fetchClubPlayersSessionSummary]', error);
    throw error;
  }

  const byPlayer = {};
  for (const row of data || []) {
    const pid = row.player_id;
    if (!pid) continue;
    if (!byPlayer[pid]) {
      byPlayer[pid] = { lastSessionDate: null, sessionIds: new Set() };
    }
    if (row.session_id) {
      byPlayer[pid].sessionIds.add(row.session_id);
    }
    if (row.created_at) {
      const prev = byPlayer[pid].lastSessionDate;
      if (!prev || new Date(row.created_at) > new Date(prev)) {
        byPlayer[pid].lastSessionDate = row.created_at;
      }
    }
  }

  const result = {};
  for (const id of ids) {
    const entry = byPlayer[id];
    result[id] = {
      sessionsPlayed: entry ? entry.sessionIds.size : 0,
      lastSessionDate: entry?.lastSessionDate ?? null,
    };
  }
  return result;
}

export const fetchPlayerRecentGames = async (playerId, clubId = null, seasonId = null, limit = 10) => {
  try {
    // First, get the player's recent games
    let query = supabase
      .from('players_session_games')
      .select(`
        id,
        created_at,
        session_game_id,
        player_id,
        team_number,
        result,
        pair_type,
        season_id,
        session_games:session_game_id(
          id,
          team_1_score,
          team_2_score,
          created_at,
          club_id,
          session_id
        )
      `)
      .eq('player_id', playerId)
      .order('created_at', { ascending: false })
      .limit(limit);

    // Apply season filter if provided
    if (seasonId) {
      query = query.eq('season_id', seasonId);
    }

    // Apply club filter if provided
    if (clubId) {
      query = query.eq('session_games.club_id', clubId);
    }

    const { data: playerGames, error } = await query;

    if (error) throw error;
    
    // For each game, fetch all players involved and their ELO ratings
    const gamesWithPlayers = await Promise.all(
      playerGames.map(async (game) => {
        if (!game.session_game_id) {
          return { ...game, teammates: [], opponents: [] };
        }
        
        // Get club and session information
        let clubName = '';
        let sessionName = '';
        
        if (game.session_games?.club_id) {
          const { data: clubData } = await supabase
            .from('clubs')
            .select('name')
            .eq('id', game.session_games.club_id)
            .single();
            
          if (clubData) {
            clubName = clubData.name;
          }
        }
        
        if (game.session_games?.session_id) {
          const { data: sessionData } = await supabase
            .from('sessions')
            .select('name')
            .eq('id', game.session_games.session_id)
            .single();
            
          if (sessionData) {
            sessionName = sessionData.name;
          }
        }
        
        // Get all players in this game directly from players_session_games with player details
        const { data: gamePlayers, error: playersError } = await supabase
          .from('players_session_games')
          .select(`
            id,
            player_id,
            team_number,
            players:player_id(
              id, 
              first_name, 
              last_name,
              gender,
              is_guest,
              is_active,
              merged_at,
              guest_merge_declined_at
            )
          `)
          .eq('session_game_id', game.session_game_id);
          
        if (playersError) {
          console.error('Error fetching game players:', playersError);
          return { ...game, teammates: [], opponents: [], clubName, sessionName };
        }
        
        // Fetch ELO ratings for this specific game from elo_history
        const { data: eloData, error: eloError } = await supabase
          .from('elo_history')
          .select('*')
          .eq('session_game_id', game.session_game_id);
          
        if (eloError) {
          console.error('Error fetching ELO history:', eloError);
        }
        
        // Create a map of player ELO ratings and changes
        const playerEloMap = {};
        if (eloData && eloData.length > 0) {
          eloData.forEach(elo => {
            // Store ELO by player_id and pair_type
            if (!playerEloMap[elo.player_id]) {
              playerEloMap[elo.player_id] = {};
            }
            playerEloMap[elo.player_id][elo.pair_type] = {
              old_value: elo.old_value,
              new_value: elo.new_value,
              elo_change: elo.elo_change
            };
          });
        }
        
        // Enhance player objects with ELO data
        const enhancedPlayers = gamePlayers.map(p => {
          const playerElos = playerEloMap[p.player_id] || {};
          // Use the game's pair type if available, otherwise use any available ELO
          const eloData = playerElos[game.pair_type] || 
                      playerElos['mixed'] || 
                      playerElos['levels'];
          
          return {
            ...p,
            players: {
              ...p.players,
              elo: eloData ? eloData.old_value : null,
              eloChange: eloData ? eloData.elo_change : null
            }
          };
        });
        
        // Group players by team
        const team1Players = enhancedPlayers
          .filter(p => p.team_number === 1 && p.players)
          .map(p => p.players);
          
        const team2Players = enhancedPlayers
          .filter(p => p.team_number === 2 && p.players)
          .map(p => p.players);
        
        // Calculate average ELO for each team
        const calcAvgElo = players => {
          const validElos = players.filter(p => p.elo !== null).map(p => p.elo);
          if (validElos.length === 0) return 1200;
          return Math.round(validElos.reduce((sum, elo) => sum + elo, 0) / validElos.length);
        };
        
        const team1Elo = calcAvgElo(team1Players);
        const team2Elo = calcAvgElo(team2Players);
        
        // Determine which team the current player was on
        const playerTeam = game.team_number;
        
        // Assign teammates and opponents
        let teammates, opponents, teamElo, opponentElo;
        if (playerTeam === 1) {
          teammates = team1Players.filter(p => p.id !== playerId);
          opponents = team2Players;
          teamElo = team1Elo;
          opponentElo = team2Elo;
        } else {
          teammates = team2Players.filter(p => p.id !== playerId);
          opponents = team1Players;
          teamElo = team2Elo;
          opponentElo = team1Elo;
        }
        
        // Find current player's ELO and ELO change
        const currentPlayerData = enhancedPlayers.find(p => p.player_id === playerId);
        const playerElo = currentPlayerData?.players?.elo || null;
        const eloChange = currentPlayerData?.players?.eloChange || null;
        
        return {
          ...game,
          teammates,
          opponents,
          teamElo,
          opponentElo,
          playerElo,
          eloChange,
          clubName,
          sessionName
        };
      })
    );
    
    return gamesWithPlayers;
  } catch (error) {
    console.error('Error fetching player recent games:', error);
    throw error;
  }
};

/**
 * Fetch the player's last 5 played sessions across all clubs
 * @param {string} playerId - Player ID
 * @param {number} limit - Number of sessions to return (default: 5)
 * @returns {Promise<Array>} - Array of recent sessions with club info, wins/losses, and ELO changes
 */
export const fetchPlayerRecentSessions = async (playerId, limit = 5, options = {}) => {
  const { includeEloDetails = true } = options || {};
  try {
    if (!playerId) return null;

    // Get all sessions where the player has played games, ordered by most recent
    const { data: sessionGames, error: gamesError } = await supabase
      .from('players_session_games')
      .select(`
        result,
        created_at,
        session_game_id,
        team_number,
        session_games:session_game_id(
          session_id,
          team_1_score,
          team_2_score,
          created_at,
          sessions:session_id(
            id,
            name,
            session_date,
            club_id,
            session_types:session_type_id(name),
            clubs:club_id(
              id,
              name,
              logo_image_url
            )
          )
        )
      `)
      .eq('player_id', playerId)
      .order('created_at', { ascending: false })
      .limit(100); // Get recent games to find most recent session

    if (gamesError) throw gamesError;
    if (!sessionGames || sessionGames.length === 0) return [];

    // Find the most recent unique session
    const sessionMap = new Map();
    
    sessionGames.forEach(game => {
      const session = game.session_games?.sessions;
      if (!session || !session.id) return;
      
      const sessionId = session.id;
      if (!sessionMap.has(sessionId)) {
        sessionMap.set(sessionId, {
          sessionId: session.id,
          sessionName: session.name || 'Unknown Session',
          sessionDate: session.session_date,
          clubId: session.club_id,
          clubName: session.clubs?.name || 'Unknown Club',
          clubLogoImageUrl: session.clubs?.logo_image_url || null,
          sessionTypeName: session.session_types?.name || null,
          gamesPlayed: 0,
          wins: 0,
          losses: 0,
          latestGameDate: null,
        });
      }
      
      const sessionData = sessionMap.get(sessionId);
      sessionData.gamesPlayed += 1;
      if (game.result === 'win') sessionData.wins += 1;
      if (game.result === 'loss') sessionData.losses += 1;

      const gameDate = game.created_at ? new Date(game.created_at) : null;
      if (gameDate && (!sessionData.latestGameDate || gameDate > sessionData.latestGameDate)) {
        sessionData.latestGameDate = gameDate;
        // Track the most recent game for score/opponent display
        const sg = game.session_games;
        if (sg && game.session_game_id) {
          const myTeam = game.team_number;
          const myScore = myTeam === 1 ? sg.team_1_score : sg.team_2_score;
          const oppScore = myTeam === 1 ? sg.team_2_score : sg.team_1_score;
          sessionData.lastGame = {
            gameId: game.session_game_id,
            playerTeam: myTeam,
            myScore,
            oppScore,
            won: game.result === 'win',
          };
        }
      }
    });

    if (sessionMap.size === 0) return [];

    // Get the most recent sessions (by latest game date)
    const sessions = Array.from(sessionMap.values());
    sessions.sort((a, b) => {
      const dateA = a.latestGameDate || (a.sessionDate ? new Date(a.sessionDate) : new Date(0));
      const dateB = b.latestGameDate || (b.sessionDate ? new Date(b.sessionDate) : new Date(0));
      return dateB - dateA;
    });

    // Get top N sessions
    const topSessions = sessions.slice(0, limit);
    if (topSessions.length === 0) return [];

    // Home activity strip etc.: skip ELO + opponent lookups (many round-trips).
    if (!includeEloDetails) {
      return topSessions.map((s) => ({
        ...s,
        mixedEloDelta: 0,
        levelsEloDelta: 0,
      }));
    }

    // Fetch ELO changes + last game opponents for all sessions in parallel
    const sessionsWithElo = await Promise.all(
      topSessions.map(async (session) => {
        try {
          const [eloResult, opponentsResult] = await Promise.all([
            supabase
              .from('elo_history')
              .select('elo_change, pair_type')
              .eq('player_id', playerId)
              .eq('session_id', session.sessionId)
              .eq('club_id', session.clubId),
            session.lastGame?.gameId
              ? supabase
                  .from('players_session_games')
                  .select('team_number, players:player_id(first_name)')
                  .eq('session_game_id', session.lastGame.gameId)
                  .neq('player_id', playerId)
              : Promise.resolve({ data: [] }),
          ]);

          // Calculate ELO deltas
          let mixedEloDelta = 0;
          let levelsEloDelta = 0;
          const eloChanges = eloResult.data || [];
          eloChanges.forEach(change => {
            if (change.pair_type === 'mixed' || change.pair_type === null) {
              mixedEloDelta += change.elo_change || 0;
            } else if (change.pair_type === 'levels') {
              levelsEloDelta += change.elo_change || 0;
            }
          });

          // Derive opponent names from last game
          const allOtherPlayers = opponentsResult.data || [];
          const opponents = allOtherPlayers
            .filter(p => p.team_number !== session.lastGame?.playerTeam)
            .map(p => p.players?.first_name || 'Unknown')
            .filter(Boolean);

          return {
            ...session,
            mixedEloDelta,
            levelsEloDelta,
            lastGame: session.lastGame ? { ...session.lastGame, opponents } : null,
          };
        } catch (err) {
          console.error('Error processing session:', session.sessionId, err);
          return {
            ...session,
            mixedEloDelta: 0,
            levelsEloDelta: 0,
          };
        }
      })
    );

    return sessionsWithElo;
  } catch (error) {
    console.error('Error fetching recent sessions:', error);
    return [];
  }
};

/**
 * @deprecated Use fetchPlayerRecentSessions instead
 * Fetch the player's most recent session across all clubs
 * @param {string} playerId - Player ID
 * @returns {Promise<Object|null>} - Most recent session with club info, wins/losses, and ELO changes, or null if none found
 */
export const fetchPlayerMostRecentSession = async (playerId) => {
  const sessions = await fetchPlayerRecentSessions(playerId, 1);
  return sessions.length > 0 ? sessions[0] : null;
}; 